// Patient form v2 rules (mirrored server-side in doctors-web-backend src/utils/patientFormRules.js + usStates.js).
import { US_STATES } from '../components/Form/StateSelectBox';

export const digitsOnly = (v) => String(v === undefined || v === null ? '' : v).replace(/\D/g, '');

let BY_NAME = null;
const byName = () => {
    if (!BY_NAME) BY_NAME = US_STATES.reduce((m, s) => ({ ...m, [s.label.toUpperCase().replace(/[^A-Z ]/g, '')]: s.value }), {});
    return BY_NAME;
};
const ALIASES = { 'VIRGIN ISLANDS': 'VI', 'US VIRGIN ISLANDS': 'VI', 'WASHINGTON DC': 'DC' };

/** 'Florida' | 'fl' | 'FL ' -> 'FL'; '' -> ''; unrecognised -> null. */
export const normalizeState = (raw) => {
    const s = String(raw === undefined || raw === null ? '' : raw).trim().toUpperCase().replace(/\./g, '').replace(/\s+/g, ' ');
    if (!s) return '';
    if (US_STATES.some((x) => x.value === s)) return s;
    const name = s.replace(/[^A-Z ]/g, '').trim();
    return byName()[name] || ALIASES[name] || null;
};

export const normalizePhone = (raw) => {
    let d = digitsOnly(raw);
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    return d;
};

/** ISO / Date / 'YYYY-MM-DD' -> 'YYYY-MM-DD' for <input type="date">. DOBs are stored at UTC midnight, so use the UTC date. */
export const dobForInput = (v) => {
    if (!v) return '';
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    if (typeof v === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v)) {
        const [m, d, y] = v.split('/');
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

export const maskSsn = (v) => {
    const d = digitsOnly(v);
    return d ? `•••-••-${d.slice(-4)}` : '';
};
export const imeiLast5 = (v) => String(v || '').slice(-5);
export const maskImei = (v) => {
    const s = String(v || '');
    return s.length > 5 ? `${'•'.repeat(s.length - 5)}${s.slice(-5)}` : s;
};

export const PROGRAM_RULES = {
    CCM: { min: 2, label: 'CCM requires at least 2 chronic conditions' },
    PCM: { exact: 1, label: 'PCM requires exactly 1 chronic condition' },
    RPM: { min: 1, label: 'RPM requires at least 1 condition' },
};
export const PROVIDER_PROGRAMS = ['RPM', 'CCM', 'PCM', 'APCM'];

export const programsFor = (patientType, enrolled = []) => {
    const set = new Set(enrolled);
    if (patientType === 'RPM' || patientType === 'Both') set.add('RPM');
    if (patientType === 'CCM' || patientType === 'Both') set.add('CCM');
    return [...set];
};
export const programRuleErrors = (programs, count) =>
    programs
        .filter((p) => PROGRAM_RULES[p])
        .filter((p) => (PROGRAM_RULES[p].exact !== undefined ? count !== PROGRAM_RULES[p].exact : count < PROGRAM_RULES[p].min))
        .map((p) => PROGRAM_RULES[p].label);

/** Field-level errors for the shared patient fields; `values.diagnosisCodes` = array of codes. */
export const validatePatientFields = (values, { programs = [], requireSsn = false, ssnTouched = true } = {}) => {
    const e = {};
    if (!String(values.city || '').trim()) e.city = 'City is required';
    const zip = digitsOnly(values.zipCode);
    if (zip && zip.length !== 5) e.zipCode = 'ZIP must be 5 digits';
    const phone = normalizePhone(values.phone1);
    if (phone && phone.length !== 10) e.phone1 = 'Phone must be 10 digits';
    if (values.state && normalizeState(values.state) === null) e.state = 'Pick a state from the list';
    if (ssnTouched) {
        const ssn = digitsOnly(values.ssn);
        if ((ssn || requireSsn) && ssn.length !== 9) e.ssn = 'SSN must be 9 digits';
    }
    const errs = programRuleErrors(programs, (values.diagnosisCodes || []).length);
    if (errs.length) e.diagnosisCodes = errs.join('; ');
    return e;
};

export const providerMissingWarning = (programs, providerId) =>
    !providerId && programs.some((p) => PROVIDER_PROGRAMS.includes(p))
        ? 'No provider selected: billing will flag this patient as "provider missing".'
        : null;

const NON_PRACTICE_SLUGS = ['vitals-portal-ops', 'unassigned'];
const NON_PRACTICE_NAMES = ['device inventory', 'unassigned'];
/** Inventory buckets / system orgs are not valid patient organizations. */
export const isPracticeOrg = (o) =>
    !!o &&
    !['inventory', 'system'].includes(o.orgType) &&
    !NON_PRACTICE_SLUGS.includes(String(o.slug || '').toLowerCase()) &&
    !NON_PRACTICE_NAMES.includes(String(o.name || '').trim().toLowerCase());
